// Translate Supabase / PostgREST errors into actionable messages for the backoffice UI.
// Postgres reports query timeouts with SQLSTATE 57014 ("canceling statement due to
// statement timeout"). PostgREST surfaces these as the same code on the error object,
// usually paired with HTTP 504 from Supabase's gateway. Network-level aborts come
// through as TypeError("Failed to fetch") or AbortError.

interface MaybeSupabaseError {
    code?: string;
    message?: string;
    name?: string;
    details?: string;
    hint?: string;
}

export interface FormattedError {
    title: string;
    detail?: string;
    // PostgREST extras when available — surfaced for easier diagnosis.
    code?: string;
    hint?: string;
    raw?: string;
    isTimeout: boolean;
}

export function formatSupabaseError(
    error: unknown,
    fallbackTitle = 'Error loading conversations'
): FormattedError {
    if (!error) {
        return { title: 'Unknown error', isTimeout: false };
    }

    const err = error as MaybeSupabaseError;
    const code = err.code ?? '';
    const message = err.message ?? '';
    const name = err.name ?? '';
    const details = err.details ?? '';
    const hint = err.hint ?? '';

    const isStatementTimeout =
        code === '57014' ||
        /statement timeout/i.test(message) ||
        /canceling statement/i.test(message);
    const isGatewayTimeout = /\b504\b/.test(message) || /gateway timeout/i.test(message);
    const isNetworkAbort =
        name === 'AbortError' || message === 'Failed to fetch' || /network ?error/i.test(message);

    if (isStatementTimeout || isGatewayTimeout) {
        return {
            title: 'Query timed out',
            detail: 'The database took too long to respond. Try narrowing the date range or removing filters.',
            code: code || undefined,
            isTimeout: true,
        };
    }

    if (isNetworkAbort) {
        return {
            title: 'Network error',
            detail: 'Could not reach the server. Check your connection and try again.',
            isTimeout: false,
        };
    }

    return {
        title: fallbackTitle,
        detail: [message, details].filter(Boolean).join(' — ') || undefined,
        code: code || undefined,
        hint: hint || undefined,
        isTimeout: false,
    };
}
